/**
 * The hold the signed-in shell puts on itself while the account's appearance
 * preferences load (App.svelte).
 *
 * The shell is held until `load()` settles or `waitMs` pass, whichever comes
 * first. The hold belongs to one signed-in user: when a different user signs
 * in — through a sign-out or straight from one account to another — a new
 * hold starts, and the previous user's load can no longer release it. Without
 * that, the first account's answer arriving after the switch would show the
 * second account's shell before its own preferences had been asked for.
 *
 * Svelte-free on purpose, so the switch can be tested under Node.
 *
 * @param {object} options
 * @param {() => Promise<unknown>} options.load - starts (or joins) the load
 *   for the current session; `loadPreferences()`
 * @param {number} options.waitMs - the longest the shell is held
 * @param {(settled: boolean) => void} options.onChange - told when the shell
 *   may show (true) or must wait (false)
 */
export function createPreferencesGate({ load, waitMs, onChange }) {
  /** The user the current hold belongs to; undefined while signed out. */
  let user;
  let hold = 0;
  let settled = false;

  const set = (value) => {
    if (settled === value) return;
    settled = value;
    onChange(value);
  };

  return {
    /** Feed every auth state change: `{ isAuthenticated, loading, userId }`. */
    update({ isAuthenticated, loading, userId = null }) {
      if (loading) return;
      if (!isAuthenticated) {
        user = undefined;
        hold += 1;
        set(false);
        return;
      }
      if (userId === user) {
        // Same session: join the load (it hydrates once per session).
        load();
        return;
      }
      user = userId;
      hold += 1;
      const mine = hold;
      set(false);
      let answered;
      try { answered = Promise.resolve(load()).catch(() => {}); } catch { answered = Promise.resolve(); }
      Promise.race([
        answered,
        new Promise((resolve) => setTimeout(resolve, waitMs)),
      ]).finally(() => {
        if (mine === hold) set(true);
      });
    },
  };
}
